/**
 * Cover art helpers — 用 avatarId（seed）穩定生成卡片封面
 *
 * 設計理念：每個角色 deterministic 一個專屬色（hash 自 avatarId），永遠不換。
 * 整體風格收斂在「cream 暖色系」5 個變體，看起來不會像每張卡跳 wild 配色。
 *
 * Persistence：第一次 render 把 seed (= avatarId) 寫回 avatar.coverSeed，之後不變。
 *   - 從 avatar.coverSeed 取 → hash → palette
 *   - 沒 coverSeed → 用 avatarId 即時 hash + 觸發 PATCH 寫回（fire-and-forget）
 */
import { patchAvatarCoverSeed } from '../../api/ivrAgents.js';

// 依 avatar 類型分色系，每類 6 個變體（hash by seed 穩定取）
// 設計：lightness 80-95% / saturation 25-40%，淡 pastel 不刺眼
// 文字統一深 ink（避免有些 fg 是淺色看起來不一致）
const PALETTES_BY_KIND = {
    // 預設 / 通用 — 暖奶油（向下相容沒傳 kind 的舊路徑）
    default: [
        { from: '#fdf9f0', to: '#f0e6cf' },
        { from: '#fbf2dc', to: '#ecdcb8' },
        { from: '#faecd6', to: '#eed7b6' },
        { from: '#fae3d2', to: '#ecceb4' },
        { from: '#fcdfd6', to: '#eecabd' },
        { from: '#f7ecdc', to: '#e8d5b3' },
    ],
    // 客服（full）— 黃色系變化
    full: [
        { from: '#fdf5d6', to: '#f0dd9c' },   // 淡米黃
        { from: '#fcecbc', to: '#ebcf7a' },   // 暖黃
        { from: '#fdf3c8', to: '#f0d990' },   // 蜂蜜黃
        { from: '#fbe9b0', to: '#e8cb7a' },   // 香檳黃
        { from: '#fef0c3', to: '#f2dc8b' },   // 奶油黃
        { from: '#fbe5a0', to: '#e3c46b' },   // 薑黃
    ],
    // 總機（transfer-only）— 淺藍系變化
    'transfer-only': [
        { from: '#e3f0fa', to: '#b4d4ec' },   // 天空藍
        { from: '#dde9f5', to: '#a8c8e7' },   // 矢車菊藍
        { from: '#e9f2f9', to: '#bedaec' },   // 薄霧藍
        { from: '#d6e7f3', to: '#a0c0e2' },   // 海洋藍
        { from: '#e6eef7', to: '#b6cce6' },   // 雪藍
        { from: '#deeaf3', to: '#aac8e0' },   // 粉藍
    ],
    // 商用帳號（超管中心 tenant 卡）— 藍色系（比總機藍稍深、帶企業感）
    business: [
        { from: '#dce8f6', to: '#9fc0e4' },   // 商務藍
        { from: '#d3e2f2', to: '#92b6dd' },   // 鋼青藍
        { from: '#e0ebf7', to: '#a9c6e6' },   // 晴空藍
        { from: '#d8e5f3', to: '#9bbade' },   // 靛灰藍
        { from: '#e4edf8', to: '#b0cae8' },   // 雲青
        { from: '#d0e0f0', to: '#8db3d9' },   // 普魯士淡藍
    ],
    // 問卷（survey）— 綠色系變化
    survey: [
        { from: '#e6f3eb', to: '#b9dcc6' },   // 抹茶
        { from: '#dfeede', to: '#aecfb0' },   // 灰綠
        { from: '#e9f4dd', to: '#c5dfa3' },   // 春芽
        { from: '#dcefe1', to: '#a9d2b4' },   // 薄荷
        { from: '#edf5d8', to: '#cbdf9a' },   // 抹茶拿鐵
        { from: '#dfecd4', to: '#b5d2a0' },   // 苔綠
    ],
};

const ANGLES = [135, 150, 165];

function hashString(s) {
    let h = 0;
    const str = String(s || '');
    for (let i = 0; i < str.length; i++) {
        h = ((h << 5) - h) + str.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h);
}

export function coverFromSeed(seed, kind = 'default') {
    const h = hashString(seed);
    const palettes = PALETTES_BY_KIND[kind] || PALETTES_BY_KIND.default;
    const palette = palettes[h % palettes.length];
    const angle = ANGLES[Math.floor(h / palettes.length) % ANGLES.length];
    return {
        gradient: `linear-gradient(${angle}deg, ${palette.from} 0%, ${palette.to} 100%)`,
        fg: '#3d362d',   // 統一深 ink，所有卡片 initials 顏色一致
    };
}

export function initialsOf(name) {
    if (!name) return '·';
    const trimmed = String(name).trim();
    if (/^[一-鿿]/.test(trimmed)) {
        return trimmed.slice(0, 2);
    }
    return trimmed.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

/**
 * 給卡片 render 用：若 avatar 沒 coverSeed，用 avatarId 即時 hash + 寫回後端。
 * 寫回後 future render 從 firestore 拿 coverSeed，render 結果完全一致。
 */
const pendingAssign = new Set();
export function ensureSeed(avatarId, onAssign) {
    if (!avatarId) return 'fallback';   // 防 empty avatarId 導致 hash 0 → 全部同色
    const seed = avatarId;
    if (!pendingAssign.has(avatarId)) {
        pendingAssign.add(avatarId);
        patchAvatarCoverSeed(avatarId, seed)
            .then(() => onAssign?.(avatarId, seed))
            .catch((e) => {
                console.warn('[coverArt] PATCH coverSeed failed:', e.message);
            });
    }
    return seed;
}
